import { inject } from '@angular/core';
import { ResolveFn, Router, UrlTree } from '@angular/router';
import { catchError, forkJoin, of, switchMap, tap } from 'rxjs';
import { UtilsService } from '../services/utils/utils.service';
import { MapService } from '../services/map/map.service';
import { DataParserService } from '../services/core/data-parser.service';
import { AccountService } from '../services/account/account.service';
import { LanguageJSON, LanguageService } from '../services/account/language.service';
import { HttpService } from '../services/core/http.service';
import { VehicleStorageService } from '../services/vehicle/vehicle-storage.service';
import { VehicleService } from '../services/vehicle/vehicle.service';

export const initResolver: ResolveFn<boolean | UrlTree> = () => {
  const utilsService = inject(UtilsService);
  const mapService = inject(MapService);
  const dataParserService = inject(DataParserService);
  const accountService = inject(AccountService);
  const languageService = inject(LanguageService);
  const httpService = inject(HttpService);
  const vehicleStorageService = inject(VehicleStorageService);
  const vehicleService = inject(VehicleService);
  const router = inject(Router);

  const language = getStoredLanguage() ?? 'en';

  
  return forkJoin({
    version: httpService.getVersion(),
    vehicleVersion: httpService.getVehiclesVersion(),
  }).pipe(
    tap(({version, vehicleVersion}) => mapService.setChargingStationVersion(version)),

    switchMap(({version, vehicleVersion}) =>
      forkJoin({
        vehiclesDto: vehicleStorageService.getVehicles(vehicleVersion),
        operatorsResp: httpService.getChargingOperators(version),
        languageJson: httpService.getLanguageJSON(language),
      }).pipe(
        tap(({ vehiclesDto, operatorsResp, languageJson }) => {
          // Operators
          const ops = (operatorsResp as any)?.operators ?? [];
          if (ops.length > 0) {
            const parsedOperators = dataParserService.parseChargingOperatorsArray(ops);
            mapService.setOperatorsArray(parsedOperators);
          }

          // Vehicles (always init, even empty)
          const parsedVehicles = dataParserService.parseVehiclesArray(vehiclesDto ?? []);
          accountService.initAllVehicles(parsedVehicles);
          vehicleService.initAllVehicles(parsedVehicles);

          // Language
          if (languageJson) {
            languageService.initLanguage(language, languageJson as LanguageJSON);
            utilsService.initTurnList();
          }
        }),

        // user vehicles must be loaded after all vehicles initialized
        switchMap(() =>
          accountService.getIsAuthorized()
            ? accountService.getUserVehicles().pipe(
              switchMap(() => of(true))) : of(true)
        )
      )
    ),

    catchError((error) => {
      router.navigate(['/init-error']);
      return of(false);
    })
  );
};

function getStoredLanguage(): string | null {
  try {
    return localStorage.getItem('language');
  } catch {
    return null;
  }
}
